import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatCurrency, formatDateForDisplay } from '@/lib/utils';
import { router } from '@inertiajs/react';
import type { CalculationRequest, CalculationRequestOptions, UserRef } from '../types';
import { ComplexityBadge, OutcomeBadge, OverdueBadge, PhaseBadge, userName } from './calculation-request-badges';
import { InlineAssigneeCell } from './inline-cells/InlineAssigneeCell';
import { InlineCalcStatusCell } from './inline-cells/InlineCalcStatusCell';

interface CalculationRequestTableProps {
    requests: CalculationRequest[];
    onUpdate?: {
        status?: (id: string, value: string) => Promise<void>;
        assignee?: (id: string, userId: string | null) => Promise<void>;
    };
    /** Only the badge catalogues and the engineer list are read here. */
    options: Pick<CalculationRequestOptions, 'statuses' | 'phases' | 'complexities' | 'engineers'>;
    canViewFinancials?: boolean;
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc' | null;
    onSortChange?: (column: string, direction: 'asc' | 'desc') => void;
    onColumnHide?: (column: string) => void;
    visibleColumns?: Set<string>;
    columnFilters?: Record<string, string[]>;
    columnFilterOptions?: Record<string, Array<{ value: string; label: string }>>;
    onFilterChange?: (column: string, values: string[]) => void;
    readOnly?: boolean;
}

/** Mirrors DEFAULT_VISIBLE_COLUMNS in pages/calculation-requests/index.tsx. */
export const DEFAULT_COLUMNS = [
    'request_number',
    'object_name',
    'company',
    'project_phase',
    'status',
    'assignee',
    'requested_calculation_date',
    'calculated_amount',
];

export function CalculationRequestTable({
    requests,
    onUpdate,
    options,
    canViewFinancials = false,
    sortColumn,
    sortDirection,
    onSortChange,
    onColumnHide,
    visibleColumns = new Set(DEFAULT_COLUMNS),
    columnFilters = {},
    columnFilterOptions = {},
    onFilterChange,
    readOnly = false,
}: CalculationRequestTableProps) {
    const { t } = useTranslations();

    const allColumns = [
        { id: 'request_number', label: t('calculation_requests.tables.request_number', 'Number') },
        { id: 'object_name', label: t('calculation_requests.tables.object_name', 'Object') },
        { id: 'company', label: t('calculation_requests.tables.company', 'Customer') },
        { id: 'project_phase', label: t('calculation_requests.tables.project_phase', 'Phase') },
        { id: 'status', label: t('calculation_requests.tables.status', 'Status') },
        { id: 'assignee', label: t('calculation_requests.tables.assignee', 'Engineer') },
        { id: 'submitted_by', label: t('calculation_requests.tables.submitted_by', 'Created by') },
        { id: 'requested_calculation_date', label: t('calculation_requests.tables.requested_calculation_date', 'Needed by') },
        { id: 'submitted_at', label: t('calculation_requests.tables.submitted_at', 'Submitted') },
        { id: 'completed_at', label: t('calculation_requests.tables.completed_at', 'Completed') },
        { id: 'complexity', label: t('calculation_requests.tables.complexity', 'Complexity') },
        { id: 'hours_spent', label: t('calculation_requests.tables.hours_spent', 'Hours') },
        { id: 'turnaround', label: t('calculation_requests.tables.turnaround', 'Turnaround') },
        { id: 'calculated_amount', label: t('calculation_requests.tables.calculated_amount', 'Calculated') },
        { id: 'sold_amount', label: t('calculation_requests.tables.sold_amount', 'Sold') },
        { id: 'outcome', label: t('calculation_requests.tables.outcome', 'Outcome') },
        { id: 'lead', label: t('calculation_requests.tables.lead', 'Lead') },
        { id: 'project', label: t('calculation_requests.tables.project', 'Project') },
        { id: 'created_at', label: t('calculation_requests.tables.created_at', 'Created') },
    ].map((column) => ({ ...column, visible: visibleColumns.has(column.id) }));

    const columns = allColumns.filter((column) => column.visible);
    const dash = t('calculation_requests.tables.none', '—');

    // Header menu items are wired only when the caller can act on them, so a table
    // rendered without sorting or column control shows no inert actions.
    const handleSort = onSortChange ? (column: string, direction: 'asc' | 'desc') => onSortChange(column, direction) : undefined;

    const money = (value: string | number | null | undefined) => {
        if (!canViewFinancials) {
            return <span className="text-muted-foreground">•••</span>;
        }

        return value === null || value === undefined ? dash : formatCurrency(Number(value));
    };

    const renderCell = (request: CalculationRequest, columnId: string) => {
        switch (columnId) {
            case 'request_number':
                return (
                    <div className="flex items-center gap-2">
                        <span className="font-medium">{request.request_number ?? dash}</span>
                        {request.is_overdue ? <OverdueBadge /> : null}
                    </div>
                );
            case 'object_name':
                return <span className="block max-w-[22rem] truncate">{request.object_name}</span>;
            case 'company':
                return <span className="block max-w-[16rem] truncate">{request.company?.name ?? dash}</span>;
            case 'project_phase':
                return <PhaseBadge value={request.project_phase} options={options.phases} />;
            case 'status':
                return (
                    <InlineCalcStatusCell
                        value={request.status}
                        statuses={options.statuses}
                        readOnly={readOnly || !onUpdate?.status}
                        onUpdate={onUpdate?.status ? (value) => onUpdate.status!(request.id, value) : undefined}
                    />
                );
            case 'assignee':
                return (
                    <InlineAssigneeCell
                        value={request.assignee}
                        engineers={options.engineers as UserRef[]}
                        readOnly={readOnly || !onUpdate?.assignee}
                        onUpdate={onUpdate?.assignee ? (userId) => onUpdate.assignee!(request.id, userId) : undefined}
                    />
                );
            case 'submitted_by':
                return userName(request.submitted_by);
            case 'requested_calculation_date':
                return (
                    <span className={cn(request.is_overdue && 'text-danger-subtle-foreground font-medium')}>
                        {request.requested_calculation_date ? formatDateForDisplay(request.requested_calculation_date) : dash}
                    </span>
                );
            case 'submitted_at':
                return request.submitted_at ? formatDateForDisplay(request.submitted_at) : dash;
            case 'completed_at':
                return request.completed_at ? formatDateForDisplay(request.completed_at) : dash;
            case 'complexity':
                return <ComplexityBadge value={request.complexity} options={options.complexities} />;
            case 'hours_spent':
                return request.hours_spent === null || request.hours_spent === undefined ? dash : Number(request.hours_spent).toFixed(1);
            case 'turnaround':
                return request.turnaround_days === null ? dash : `${request.turnaround_days} ${t('calculation_requests.show.timeline.days', 'days')}`;
            case 'calculated_amount':
                return money(request.calculated_amount);
            case 'sold_amount':
                return money(request.sold_amount);
            case 'outcome':
                return <OutcomeBadge value={request.outcome} />;
            case 'lead':
                return request.lead?.lead_number ?? dash;
            case 'project':
                return request.project?.contract_number ?? dash;
            case 'created_at':
                return request.created_at ? formatDateForDisplay(request.created_at) : dash;
            default:
                return null;
        }
    };

    // Inline cells manage their own click handling, so the row-level navigation must not
    // fire for them.
    const interactiveColumns = new Set(['status', 'assignee']);

    return (
        <div className="bg-card overflow-hidden rounded-xl border">
            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((column) => {
                            const isSorted = sortColumn === column.id;
                            const selectedFilters = columnFilters[column.id] || [];

                            return (
                                <TableHead key={column.id}>
                                    <TableColumnHeader
                                        label={column.label}
                                        sortDirection={isSorted ? sortDirection : null}
                                        onSortAscending={handleSort ? () => handleSort(column.id, 'asc') : undefined}
                                        onSortDescending={handleSort ? () => handleSort(column.id, 'desc') : undefined}
                                        onHide={onColumnHide ? () => onColumnHide(column.id) : undefined}
                                        filterOptions={columnFilterOptions[column.id] || []}
                                        selectedFilters={selectedFilters}
                                        onFilterChange={(values) => onFilterChange?.(column.id, values)}
                                        isFiltered={selectedFilters.length > 0}
                                    />
                                </TableHead>
                            );
                        })}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {requests.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="text-muted-foreground py-12 text-center">
                                {t('calculation_requests.index.empty.title', 'No calculation requests')}
                            </TableCell>
                        </TableRow>
                    ) : (
                        requests.map((request) => (
                            <TableRow key={request.id} className="cursor-pointer" onClick={() => router.visit(`/calculation-requests/${request.id}`)}>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        className={cn(interactiveColumns.has(column.id) && 'p-1')}
                                        onClick={interactiveColumns.has(column.id) ? (event) => event.stopPropagation() : undefined}
                                    >
                                        {renderCell(request, column.id)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
}
